import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { ButtonVariant } from '~/chords';
import { Button } from '~/components/Button';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useBrand } from '~/modules/branding';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { SETTINGS_ROUTES } from '~settings/config/routes';

const buttonsMap = {
  canCreateLabel: {
    titleKey: 'label.addLink',
    to: () => SETTINGS_ROUTES.LABEL_CREATE,
    variant: 'primary',
  },
  canEditLabel: {
    titleKey: 'label.editLabel',
    to: (id: string) => SETTINGS_ROUTES.LABEL_EDIT(id),
    variant: 'secondary',
  },
  canRequestLabel: {
    titleKey: 'sublabelRequest.requestSublabelLink',
    to: () => SETTINGS_ROUTES.SUBLABEL_REQUEST_CREATE,
    variant: 'primary',
  },
};

interface Props {
  id?: string;
  isSublabel?: boolean;
}

export function ManageLabelButton({ id = '', isSublabel = false }: Props) {
  const brand = useBrand();
  const { t } = useTranslation('settings');
  const { hasPermission } = useUser();
  const [manageLabelEnabled, containerSettingsSublabelRequest] =
    useFeatureFlags([
      FEATURE_FLAGS.ContainerSettingsManageLabel,
      FEATURE_FLAGS.ContainerSettingsSublabelRequest,
    ]);

  const canRequestLabel = hasPermission('LABEL_REQUEST.CREATE');

  const canCreateLabel = hasPermission('LABEL.CREATE', {
    id: brand.name,
    type: 'APPLICATION',
  });

  const canEditLabel = hasPermission('LABEL.EDIT', {
    id: brand.name,
    type: 'APPLICATION',
  });

  let btnToRender = null;

  /*
  if id is provided & has edit permission -> show Edit
  if user has permission to create label -> show Create
  if user has permission to request label -> show Request
   */
  if (id && canEditLabel) {
    btnToRender = buttonsMap.canEditLabel;
  } else if (canCreateLabel) {
    btnToRender = buttonsMap.canCreateLabel;
  } else if (
    containerSettingsSublabelRequest &&
    canRequestLabel &&
    !isSublabel
  ) {
    btnToRender = buttonsMap.canRequestLabel;
  }

  if (!btnToRender || !manageLabelEnabled) {
    return null;
  }

  return (
    <Button
      asChild
      variant={btnToRender.variant as ButtonVariant}
      className="shrink-0 self-center"
      size="large"
    >
      <Link to={btnToRender.to(id)}>{t(btnToRender.titleKey)}</Link>
    </Button>
  );
}

ManageLabelButton.displayName = 'ManageLabelButton';
